/**
 * A date the team has scheduled a customer's request for
 * (concierge_inquiries.scheduled_for, migration 164) — for example, a
 * confirmation number release set for a specific day.
 *
 * Once it's set, that date is the promise: the customer's trip card shows it
 * instead of the usual timing (ServiceInquiryCard), the concierge quotes it
 * from the trip grounding (tommy-booking-context.js), and the request's
 * internal deadline (`due_by`) moves to the end of the team's working day on
 * it (scheduleInquiry in server/concierge-inquiries.js). One reading of the
 * date for all of them; plain JS, so the browser and Node share it.
 *
 * Dates are calendar days ("2026-10-14"), not instants: they're labelled
 * without any timezone shift, and "today" is the team's (Pacific) today.
 */
import { RELEASE_TEAM_TIMEZONE, TEAM_DAY_END_HOUR } from '../../supabase/functions/_shared/confirmation-release-window.js';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `{ year, month, day }` for a real "YYYY-MM-DD" calendar date, else null. */
export function parseScheduledDate(value) {
  const m = DATE_RE.exec(String(value ?? '').trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return { year, month, day };
}

/** "Wednesday, October 14" — or "Wednesday, October 14, 2026" with `withYear`. */
export function scheduledDateLabel(value, { withYear = false } = {}) {
  const p = parseScheduledDate(value);
  if (!p) return null;
  return new Date(Date.UTC(p.year, p.month - 1, p.day)).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

const teamFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: RELEASE_TEAM_TIMEZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function teamParts(date) {
  const p = Object.fromEntries(teamFormatter.formatToParts(date).map((x) => [x.type, x.value]));
  return { year: Number(p.year), month: Number(p.month), day: Number(p.day), hour: Number(p.hour) % 24, minute: Number(p.minute) };
}

/** Today on the team's clock, as "YYYY-MM-DD". */
export function teamToday(now = new Date()) {
  const p = teamParts(now);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** The calendar day `n` days after "YYYY-MM-DD", as "YYYY-MM-DD". */
export function addDaysToDate(value, n) {
  const p = parseScheduledDate(value);
  if (!p) return null;
  return new Date(Date.UTC(p.year, p.month - 1, p.day + n)).toISOString().slice(0, 10);
}

/** The end of the team's working day on that date: the request's internal deadline. */
export function scheduledDayEnd(value) {
  const p = parseScheduledDate(value);
  if (!p) return null;
  const wall = Date.UTC(p.year, p.month - 1, p.day, TEAM_DAY_END_HOUR);
  let guess = wall;
  // The zone's offset at the guess, applied until it settles (twice covers DST).
  for (let i = 0; i < 2; i += 1) {
    const t = teamParts(new Date(guess));
    guess = wall - (Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute) - guess);
  }
  return new Date(guess);
}
